import { useCallback, useEffect, useState } from 'react'
import { fetchAnalytics, fetchPnLHistory, type PnLAnalytics, type RecentActivityEntry } from '../../lib/api.ts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

function formatPnL(value: number | null | undefined): string {
  const v = value ?? 0
  const sign = v >= 0 ? '+' : ''
  return `${sign}$${Math.abs(v).toFixed(2)}`
}

function pnlClass(value: number | null | undefined): string {
  if ((value ?? 0) > 0) return 'pnl-positive'
  if ((value ?? 0) < 0) return 'pnl-negative'
  return 'pnl-neutral'
}

function ResultBadge({ pnl }: { pnl: number }) {
  const isWin = pnl >= 0
  return <Badge variant={isWin ? 'default' : 'destructive'}>{isWin ? 'WIN' : 'LOSS'}</Badge>
}

export function PnLOverview() {
  const [analytics, setAnalytics] = useState<PnLAnalytics | null>(null)
  const [activity, setActivity] = useState<RecentActivityEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [analyticsRes, historyRes] = await Promise.all([
        fetchAnalytics(),
        fetchPnLHistory(),
      ])
      if (analyticsRes.success) setAnalytics(analyticsRes.analytics)
      setActivity(historyRes.recentActivity ?? [])
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load P&L data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, 10000)
    return () => clearInterval(interval)
  }, [load])

  const overall = analytics?.overall

  return (
    <div className="animate-fade-in-up">
      <div className="page-header">
        <div>
          <div className="page-title">Analytics</div>
          <div className="page-title-main">P&L Overview</div>
        </div>
        <div className="page-actions">
          <Button variant="ghost" size="sm" onClick={load} type="button">Refresh</Button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', color: 'var(--red)', fontFamily: 'var(--font-mono)', fontSize: '11px', background: 'var(--red-muted)', border: '1px solid var(--red)', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Stat Cards Row */}
      {overall && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '24px' }}>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">Total Trades</div>
              <div className="stat-card-value">{overall.totalTrades}</div>
              <div className="stat-card-sub" style={{ color: 'var(--text-secondary)' }}>
                {overall.activeTrades} active
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">Win Rate</div>
              <div className={`stat-card-value ${overall.winRate >= 50 ? 'pnl-positive' : 'pnl-negative'}`}>
                {(overall.winRate ?? 0).toFixed(1)}%
              </div>
              <div className="stat-card-sub">
                <span style={{ color: 'var(--green)' }}>{overall.wins}W</span>
                {' / '}
                <span style={{ color: 'var(--red)' }}>{overall.losses}L</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">Total P&L</div>
              <div className={`stat-card-value ${pnlClass(overall.totalPnL)}`}>
                {formatPnL(overall.totalPnL)}
              </div>
              <div className="stat-card-sub">
                avg {formatPnL(overall.avgPnL)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">ROI</div>
              <div className={`stat-card-value ${pnlClass(overall.roi)}`}>
                {(overall.roi ?? 0) >= 0 ? '+' : ''}{(overall.roi ?? 0).toFixed(2)}%
              </div>
              <div className="stat-card-sub">
                ${(overall.totalInvested ?? 0).toFixed(2)} invested
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Best / Worst trade row */}
      {analytics && (analytics.bestTrade || analytics.worstTrade) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '24px' }}>
          {analytics.bestTrade && (
            <Card>
              <CardHeader>
                <CardTitle style={{ color: 'var(--green)' }}>↑ Best Trade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-mono" style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {analytics.bestTrade.crypto} · {analytics.bestTrade.date}
                </div>
                <div className="pnl-positive" style={{ fontSize: '20px', fontWeight: 700 }}>
                  {formatPnL(analytics.bestTrade.pnl)}
                </div>
                <div className="font-mono" style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  ROI: +{(analytics.bestTrade.roi ?? 0).toFixed(1)}% · ${(analytics.bestTrade.invested ?? 0).toFixed(2)} invested
                </div>
              </CardContent>
            </Card>
          )}
          {analytics.worstTrade && (
            <Card>
              <CardHeader>
                <CardTitle style={{ color: 'var(--red)' }}>↓ Worst Trade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-mono" style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {analytics.worstTrade.crypto} · {analytics.worstTrade.date}
                </div>
                <div className="pnl-negative" style={{ fontSize: '20px', fontWeight: 700 }}>
                  {formatPnL(analytics.worstTrade.pnl)}
                </div>
                <div className="font-mono" style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  ROI: {(analytics.worstTrade.roi ?? 0).toFixed(1)}% · ${(analytics.worstTrade.invested ?? 0).toFixed(2)} invested
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Recent Activity Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Recent Activity</span>
            {loading && (
              <span className="font-mono" style={{ fontSize: '9px', color: 'var(--text-muted)' }}>LOADING…</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {activity.length === 0 && !loading ? (
            <div className="empty-state">
              <span className="empty-state-text">No recent activity</span>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Result</th>
                    <th>Market / Crypto</th>
                    <th>Status</th>
                    <th className="num">Invested</th>
                    <th className="num">P&L</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((entry) => (
                    <tr key={entry.id}>
                      <td><ResultBadge pnl={entry.pnl} /></td>
                      <td>
                        <span className="font-mono" style={{ fontSize: '11px' }}>
                          {entry.crypto}
                        </span>
                      </td>
                      <td>
                        <span className="font-mono" style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                          {entry.status}
                        </span>
                      </td>
                      <td className="num">
                        <span className="mono-num">${(entry.invested ?? 0).toFixed(2)}</span>
                      </td>
                      <td className="num">
                        <span className={pnlClass(entry.pnl)}>{formatPnL(entry.pnl)}</span>
                      </td>
                      <td>
                        <span className="font-mono" style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          {entry.date}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
